/* ============================================================
 * DADDL Bundle popup – <daddl-bundle-popup>
 * ============================================================
 * Site-wide delayed full-screen offer popup (rendered from
 * layout/theme.liquid via sections/daddl-bundle-popup.liquid).
 *
 * - The countdown is a PAUSABLE accumulator, not a plain timeout:
 *   it only ticks while the page is fully loaded, the tab is
 *   visible, and no other popup/drawer/modal is open (email popup,
 *   cart drawer, menu drawer, bundle builder, or anything else that
 *   locks body scroll). It never stacks on another popup – it waits
 *   its turn and resumes counting when the way is clear.
 * - CTA: opens the bundle builder modal when one exists on the page
 *   (product pages); otherwise navigates to the configured fallback
 *   link.
 * - Dismissals are frequency-capped in localStorage.
 * - In the theme editor it only shows while its section is selected.
 * ============================================================ */

(function () {
  'use strict';

  if (customElements.get('daddl-bundle-popup')) return;

  const STORAGE_KEY = 'daddl_bundle_popup_v1';
  const TICK_MS = 500;

  /* True while something else owns the screen: any element that locks body
   * scroll (Dawn drawers, the bundle modal, the email popup's inline
   * overflow style) or known open overlays. */
  function somethingElseOpen() {
    if (document.body.classList.contains('overflow-hidden')) return true;
    if (document.body.style.overflow === 'hidden') return true;
    if (document.documentElement.style.overflow === 'hidden') return true;

    const emailPopup = document.getElementById('daddl-popup-overlay');
    if (emailPopup && !emailPopup.hidden) return true;

    if (document.querySelector('cart-drawer.active, cart-drawer.animate')) return true;
    if (document.querySelector('details[open] > .menu-drawer, .menu-opening')) return true;
    if (document.querySelector('daddl-bundle-builder.is-open')) return true;

    return false;
  }

  class DaddlBundlePopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.querySelectorAll('[data-popup-close]').forEach((el) => {
        el.addEventListener('click', () => this.dismiss());
      });

      const cta = this.querySelector('[data-popup-cta]');
      if (cta) {
        cta.addEventListener('click', () => {
          this.dismiss();
          const builder = document.querySelector('daddl-bundle-builder');
          if (builder && typeof builder.open === 'function') {
            builder.open(cta);
          } else if (this.dataset.fallbackUrl) {
            window.location.href = this.dataset.fallbackUrl;
          }
        });
      }

      this.onKeyDown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.dismiss();
      };

      /* Theme editor: only show while the section is selected. */
      if (window.Shopify && Shopify.designMode) {
        const sectionEl = this.closest('.shopify-section');
        if (sectionEl) {
          sectionEl.addEventListener('shopify:section:select', () => this.show());
          sectionEl.addEventListener('shopify:section:deselect', () => this.hide());
        }
        return;
      }

      if (!this.shouldShow()) return;

      /* Pausable countdown: accumulate visible-and-unblocked time only,
       * starting once the page has fully loaded. */
      this.delayMs = (parseInt(this.dataset.delay, 10) || 15) * 1000;
      this.elapsedMs = 0;
      const startTicking = () => {
        this.ticker = window.setInterval(() => this.tick(), TICK_MS);
      };
      if (document.readyState === 'complete') {
        startTicking();
      } else {
        window.addEventListener('load', startTicking, { once: true });
      }
    }

    disconnectedCallback() {
      if (this.ticker) window.clearInterval(this.ticker);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    tick() {
      if (document.visibilityState !== 'visible') return;
      if (somethingElseOpen()) return;

      this.elapsedMs += TICK_MS;
      if (this.elapsedMs < this.delayMs) return;

      window.clearInterval(this.ticker);
      this.ticker = null;
      this.show();
    }

    shouldShow() {
      try {
        const state = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (!state.timestamp) return true;
        const days = parseInt(this.dataset.dismissDays, 10) || 3;
        return (Date.now() - state.timestamp) / 86400000 > days;
      } catch (err) {
        return true;
      }
    }

    show() {
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeyDown);
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));

      const close = this.querySelector('.daddl-bundle-popup__close');
      if (typeof trapFocus === 'function') {
        trapFocus(this, close);
      } else if (close) {
        close.focus();
      }
    }

    /* Hide without recording a dismissal (theme editor deselect). */
    hide() {
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeyDown);
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.classList.remove('is-open');
      window.setTimeout(() => {
        this.hidden = true;
      }, 350);
    }

    dismiss() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now() }));
      } catch (err) {
        /* noop */
      }
      this.hide();
    }
  }

  customElements.define('daddl-bundle-popup', DaddlBundlePopup);
})();
